import React, { useState, useEffect } from 'react';
import {
  ReceiptText,
  RefreshCw,
  Eye,
  MessageCircle,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  SlidersHorizontal,
  X,
  FileDown,
  Check,
  Ban
} from 'lucide-react';
import './Refunds.css';

const Refunds = () => {
  const [activeTab, setActiveTab] = useState('refund-requests');
  const [refunds, setRefunds] = useState([]);
  const [refundRequests, setRefundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
    startDate: '',
    endDate: ''
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  const loadRefunds = async () => {
    try {
      setLoading(true);
      setError('');
      const queryParams = new URLSearchParams({
        page: pagination.current,
        limit: 20,
        ...filters
      });

      const response = await fetch(`/api/admin/refunds?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setRefunds(data.refunds || []);
        if (data.pagination) {
          setPagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.error || 'Failed to load refunds');
      }
    } catch (err) {
      setError('Failed to load refunds');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadRefundRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const queryParams = new URLSearchParams({
        page: pagination.current,
        limit: 20,
        ...filters
      });

      const response = await fetch(`/api/refund-requests/admin/refund-requests?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setRefundRequests(data.refunds || []);
        if (data.pagination) {
          setPagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.error || 'Failed to load refund requests');
      }
    } catch (err) {
      setError('Failed to load refund requests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/refund-requests/admin/stats', {
        credentials: 'include'
      });
      const data = await response.json();
      if (data.success) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Failed to load refund stats:', err);
    }
  };

  const promptApprove = (id) => {
    setConfirmAction({
      id,
      action: 'approve',
      title: 'Approve Refund Request',
      message: 'Are you sure you want to approve this refund request? This will process the refund to the customer.',
      confirmText: 'Approve & Process',
      confirmClass: 'btn-success'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const promptReject = (id) => {
    setConfirmAction({
      id,
      action: 'reject',
      title: 'Reject Refund Request',
      message: 'Are you sure you want to reject this refund request? Please provide a reason.',
      confirmText: 'Reject',
      confirmClass: 'btn-danger'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const executeAction = async () => {
    if (!confirmAction) return;

    const { id, ids, action } = confirmAction;

    try {
      // Handle bulk actions
      if (action === 'bulk-approve') {
        const results = await Promise.all(
          ids.map(itemId =>
            fetch(`/api/refund-requests/admin/process/${itemId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                adminNotes: actionNotes || 'Bulk approved by admin'
              })
            }).then(r => r.json())
          )
        );

        const successCount = results.filter(r => r.success).length;
        setSuccess(`${successCount} of ${ids.length} refund requests approved`);
        setTimeout(() => setSuccess(''), 3000);
        setSelectedItems([]);
        loadRefundRequests();
        loadStats();
        setShowConfirmDialog(false);
        setConfirmAction(null);
        setActionNotes('');
        return;
      }

      if (action === 'bulk-reject') {
        if (!actionNotes.trim()) {
          setError('Please provide a reason for bulk rejection');
          setTimeout(() => setError(''), 3000);
          return;
        }

        const results = await Promise.all(
          ids.map(itemId =>
            fetch(`/api/refund-requests/admin/reject/${itemId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                rejectionReason: actionNotes
              })
            }).then(r => r.json())
          )
        );

        const successCount = results.filter(r => r.success).length;
        setSuccess(`${successCount} of ${ids.length} refund requests rejected`);
        setTimeout(() => setSuccess(''), 3000);
        setSelectedItems([]);
        loadRefundRequests();
        loadStats();
        setShowConfirmDialog(false);
        setConfirmAction(null);
        setActionNotes('');
        return;
      }

      // Single item actions
      if (action === 'approve') {
        const response = await fetch(`/api/refund-requests/admin/process/${id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            adminNotes: actionNotes || 'Approved by admin'
          })
        });

        const data = await response.json();
        if (data.success) {
          setSuccess('Refund request approved and processed');
          setTimeout(() => setSuccess(''), 3000);
          loadRefundRequests();
          loadStats();
        } else {
          setError(data.error || 'Failed to approve');
          setTimeout(() => setError(''), 3000);
        }
      } else if (action === 'reject') {
        if (!actionNotes.trim()) {
          setError('Please provide a reason for rejection');
          setTimeout(() => setError(''), 3000);
          return;
        }

        const response = await fetch(`/api/refund-requests/admin/reject/${id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            rejectionReason: actionNotes
          })
        });

        const data = await response.json();
        if (data.success) {
          setSuccess('Refund request rejected');
          setTimeout(() => setSuccess(''), 3000);
          loadRefundRequests();
          loadStats();
        } else {
          setError(data.error || 'Failed to reject');
          setTimeout(() => setError(''), 3000);
        }
      }

      setShowConfirmDialog(false);
      setConfirmAction(null);
      setActionNotes('');
    } catch (err) {
      setError(`Failed to ${action}`);
      setTimeout(() => setError(''), 3000);
    }
  };

  const cancelAction = () => {
    setShowConfirmDialog(false);
    setConfirmAction(null);
    setActionNotes('');
  };

  const toggleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(itemId => itemId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (items) => {
    if (selectedItems.length === items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(items.map(item => item.id));
    }
  };

  const handleBulkApprove = () => {
    if (selectedItems.length === 0) {
      setError('Please select items to approve');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setConfirmAction({
      ids: selectedItems,
      action: 'bulk-approve',
      title: `Approve ${selectedItems.length} Refund Request${selectedItems.length > 1 ? 's' : ''}`,
      message: `Are you sure you want to approve ${selectedItems.length} refund requests?`,
      confirmText: 'Approve All',
      confirmClass: 'btn-success'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const handleBulkReject = () => {
    if (selectedItems.length === 0) {
      setError('Please select items to reject');
      setTimeout(() => setError(''), 3000);
      return;
    }

    setConfirmAction({
      ids: selectedItems,
      action: 'bulk-reject',
      title: `Reject ${selectedItems.length} Refund Request${selectedItems.length > 1 ? 's' : ''}`,
      message: `Are you sure you want to reject ${selectedItems.length} refund requests? Please provide a reason.`,
      confirmText: 'Reject All',
      confirmClass: 'btn-danger'
    });
    setActionNotes('');
    setShowConfirmDialog(true);
  };

  const exportToCSV = () => {
    let data = [];
    let filename = '';
    let headers = [];

    if (activeTab === 'refunds') {
      headers = ['ID', 'Order', 'Customer', 'Amount', 'Reason', 'Status', 'Date'];
      data = refunds.map(r => [
        r.id,
        r.order?.orderNumber || 'N/A',
        `${r.customer?.firstName || ''} ${r.customer?.lastName || ''}`.trim() || 'N/A',
        r.refundAmount,
        r.refundReason || 'N/A',
        r.status,
        new Date(r.createdAt).toLocaleString()
      ]);
      filename = `refunds-${new Date().toISOString().split('T')[0]}.csv`;
    } else {
      headers = ['ID', 'Order', 'Customer', 'Merchant', 'Amount', 'Reason', 'Status', 'Date'];
      data = refundRequests.map(r => [
        r.id,
        r.order?.orderNumber || 'N/A',
        `${r.customer?.firstName || ''} ${r.customer?.lastName || ''}`.trim() || 'N/A',
        r.merchant?.username || 'N/A',
        r.refundAmount,
        r.refundReason || 'N/A',
        r.status,
        new Date(r.createdAt).toLocaleString()
      ]);
      filename = `refund-requests-${new Date().toISOString().split('T')[0]}.csv`;
    }

    const csvContent = [
      headers.join(','),
      ...data.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSuccess('Data exported successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const viewDetails = (item, type) => {
    setSelectedItem({ ...item, type });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  useEffect(() => {
    loadStats();
  }, []);

  // Reset to page 1 when filters or tab changes
  useEffect(() => {
    setPagination(prev => ({ ...prev, current: 1 }));
  }, [activeTab, filters.status, filters.startDate, filters.endDate]);

  // Load data when page or tab changes
  useEffect(() => {
    if (activeTab === 'refunds') {
      loadRefunds();
    } else {
      loadRefundRequests();
    }
    setSelectedItems([]);
  }, [activeTab, pagination.current]); // eslint-disable-line react-hooks/exhaustive-deps

  const getStatusIcon = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case 'completed':
      case 'approved':
        return <CheckCircle2 size={14} />;
      case 'pending':
      case 'pending_merchant':
      case 'pending_admin':
        return <Clock size={14} />;
      case 'processing':
        return <RefreshCw size={14} className="ref-spin" />;
      case 'failed':
      case 'rejected':
        return <XCircle size={14} />;
      default:
        return <AlertCircle size={14} />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const normalizeResponseAction = (action) => {
    const normalized = String(action || '').trim().toLowerCase();
    if (['approve', 'approved', 'accept', 'accepted', 'processing', 'completed'].includes(normalized)) return 'approved';
    if (['reject', 'rejected', 'decline', 'declined'].includes(normalized)) return 'rejected';
    if (normalized === 'queued') return 'queued';
    return normalized || 'response';
  };

  const getResponseTitle = (actor, response) => {
    const action = normalizeResponseAction(response?.action || response);
    if (action === 'approved') return `${actor} approved`;
    if (action === 'rejected') return `${actor} rejected`;
    if (action === 'queued') return `${actor} queued`;
    return `${actor} response`;
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      startDate: '',
      endDate: ''
    });
  };

  const pendingAdminCount = refundRequests.filter(r => r.status === 'PENDING_ADMIN').length;

  return (
    <div className="ref-container">
      {/* Page Header */}
      <div className="ref-header">
        <div className="ref-header-content">
          <div className="ref-header-text">
            <h1><ReceiptText size={24} /> Refunds Management</h1>
            <p className="ref-subtitle">Process customer refund requests and track refund history</p>
          </div>
          <button
            onClick={() => {
              loadStats();
              if (activeTab === 'refunds') loadRefunds();
              else loadRefundRequests();
            }}
            className="btn btn-secondary"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="ref-stats-grid">
          <div className="ref-stat-card">
            <div className="ref-stat-icon pending">
              <Clock size={24} />
            </div>
            <div className="ref-stat-content">
              <span className="ref-stat-value">{stats.pendingAdmin || 0}</span>
              <span className="ref-stat-label">Awaiting Review</span>
            </div>
          </div>
          <div className="ref-stat-card">
            <div className="ref-stat-icon processing">
              <RefreshCw size={24} />
            </div>
            <div className="ref-stat-content">
              <span className="ref-stat-value">{stats.pendingMerchant || 0}</span>
              <span className="ref-stat-label">With Merchant</span>
            </div>
          </div>
          <div className="ref-stat-card">
            <div className="ref-stat-icon completed">
              <CheckCircle2 size={24} />
            </div>
            <div className="ref-stat-content">
              <span className="ref-stat-value">{stats.approved || 0}</span>
              <span className="ref-stat-label">Approved</span>
            </div>
          </div>
          <div className="ref-stat-card">
            <div className="ref-stat-icon failed">
              <XCircle size={24} />
            </div>
            <div className="ref-stat-content">
              <span className="ref-stat-value">{stats.rejected || 0}</span>
              <span className="ref-stat-label">Rejected</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="ref-tabs">
        <button
          onClick={() => setActiveTab('refund-requests')}
          className={`ref-tab ${activeTab === 'refund-requests' ? 'active' : ''}`}
        >
          <MessageCircle size={16} />
          Refund Requests
          {pendingAdminCount > 0 && (
            <span className="ref-tab-badge">{pendingAdminCount}</span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('refunds')}
          className={`ref-tab ${activeTab === 'refunds' ? 'active' : ''}`}
        >
          <ReceiptText size={16} />
          Processed Refunds
        </button>
      </div>

      {/* Filters */}
      <div className="ref-filters">
        <div className="ref-filters-header">
          <SlidersHorizontal size={16} />
          <h3>Filters</h3>
        </div>
        <div className="ref-filters-grid">
          <div className="ref-filter-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">All Status</option>
              {activeTab === 'refund-requests' ? (
                <>
                  <option value="PENDING_MERCHANT">Pending Merchant</option>
                  <option value="PENDING_ADMIN">Pending Admin</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                </>
              ) : (
                <>
                  <option value="PENDING">Pending</option>
                  <option value="PROCESSING">Processing</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="FAILED">Failed</option>
                </>
              )}
            </select>
          </div>
          <div className="ref-filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
            />
          </div>
          <div className="ref-filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
            />
          </div>
          <div className="ref-filter-actions">
            <button onClick={clearFilters} className="btn btn-secondary">
              <X size={18} />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="ref-alert ref-alert-danger">
          <AlertCircle size={18} />
          {error}
        </div>
      )}
      {success && (
        <div className="ref-alert ref-alert-success">
          <CheckCircle2 size={18} />
          {success}
        </div>
      )}

      {/* Content */}
      <div className="ref-table-container">
        <div className="ref-table-header">
          <h3>
            {activeTab === 'refunds' ? 'Processed Refunds' : 'Refund Requests'}
            {selectedItems.length > 0 && (
              <span className="ref-selected-count">
                ({selectedItems.length} selected)
              </span>
            )}
          </h3>
          <div className="ref-table-actions">
            {selectedItems.length > 0 && activeTab === 'refund-requests' && (
              <>
                <button onClick={handleBulkApprove} className="btn btn-success btn-sm">
                  <CheckCircle2 size={16} />
                  Approve Selected
                </button>
                <button onClick={handleBulkReject} className="btn btn-danger btn-sm">
                  <Ban size={16} />
                  Reject Selected
                </button>
              </>
            )}
            <button
              onClick={exportToCSV}
              className="btn btn-primary btn-sm"
              disabled={
                (activeTab === 'refunds' && refunds.length === 0) ||
                (activeTab === 'refund-requests' && refundRequests.length === 0)
              }
            >
              <FileDown size={16} />
              Export CSV
            </button>
          </div>
        </div>

        {loading ? (
          <div className="ref-loading">
            <div className="ref-spinner"></div>
            <p className="ref-loading-text">Loading {activeTab === 'refunds' ? 'refunds' : 'refund requests'}...</p>
          </div>
        ) : (
          <div className="ref-table-wrapper">
            {activeTab === 'refunds' && (
              <table className="ref-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Order</th>
                    <th>Customer</th>
                    <th>Amount</th>
                    <th>Type</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {refunds.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="ref-empty">
                        <ReceiptText size={48} />
                        <p>No refunds found</p>
                        <div className="ref-empty-text">Processed refunds will appear here</div>
                      </td>
                    </tr>
                  ) : (
                    refunds.map((refund) => (
                      <tr key={refund.id}>
                        <td className="ref-text-mono">{refund.id?.slice(-8) || 'N/A'}</td>
                        <td className="ref-text-mono">{refund.order?.orderNumber || 'N/A'}</td>
                        <td>
                          <div className="ref-text-primary">{refund.customer?.firstName} {refund.customer?.lastName}</div>
                          <div className="ref-text-secondary">{refund.customer?.email}</div>
                        </td>
                        <td className="ref-text-primary">{formatCurrency(refund.refundAmount)}</td>
                        <td>
                          <span className="ref-badge processing">{refund.refundType || 'Full'}</span>
                        </td>
                        <td className="ref-text-secondary ref-truncate">
                          {refund.refundReason || 'N/A'}
                        </td>
                        <td>
                          <span className={`ref-badge ${refund.status?.toLowerCase() || 'pending'}`}>
                            {getStatusIcon(refund.status)}
                            <span>{refund.status?.replace('_', ' ') || 'Pending'}</span>
                          </span>
                        </td>
                        <td className="ref-text-secondary">{formatDate(refund.createdAt)}</td>
                        <td>
                          <button
                            onClick={() => viewDetails(refund, 'refund')}
                            className="btn btn-icon"
                          >
                            <Eye size={16} />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}

            {activeTab === 'refund-requests' && (
              <table className="ref-table">
                <thead>
                  <tr>
                    <th className="ref-checkbox-cell">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === refundRequests.filter(r => r.status === 'PENDING_ADMIN').length && refundRequests.filter(r => r.status === 'PENDING_ADMIN').length > 0}
                        onChange={() => toggleSelectAll(refundRequests.filter(r => r.status === 'PENDING_ADMIN'))}
                        className="ref-checkbox"
                      />
                    </th>
                    <th>Request</th>
                    <th>Customer</th>
                    <th>Merchant</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {refundRequests.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="ref-empty">
                        <MessageCircle size={48} />
                        <p>No refund requests found</p>
                        <div className="ref-empty-text">Customer refund requests will appear here</div>
                      </td>
                    </tr>
                  ) : (
                    refundRequests.map((request) => (
                      <tr key={request.id} className={request.status === 'PENDING_ADMIN' ? 'ref-highlight' : ''}>
                        <td className="ref-checkbox-cell">
                          <input
                            type="checkbox"
                            checked={selectedItems.includes(request.id)}
                            onChange={() => toggleSelectItem(request.id)}
                            className="ref-checkbox"
                            disabled={request.status !== 'PENDING_ADMIN'}
                          />
                        </td>
                        <td>
                          <div className="ref-text-mono">#{request.id?.slice(0, 8) || 'N/A'}</div>
                          <div className="ref-text-secondary">{formatDate(request.createdAt)}</div>
                        </td>
                        <td>
                          <div className="ref-text-primary">{request.customer?.firstName} {request.customer?.lastName}</div>
                          <div className="ref-text-secondary">{request.customer?.email}</div>
                        </td>
                        <td>
                          <div className="ref-text-primary">{request.merchant?.username || 'N/A'}</div>
                          <div className="ref-text-secondary">{request.merchant?.email}</div>
                        </td>
                        <td>
                          <div className="ref-amount">{formatCurrency(request.refundAmount)}</div>
                          <div className="ref-text-secondary ref-truncate">{request.refundReason?.substring(0, 25) || 'N/A'}{request.refundReason?.length > 25 ? '...' : ''}</div>
                        </td>
                        <td>
                          <span className={`ref-badge ${request.status?.toLowerCase() || 'pending'}`}>
                            {getStatusIcon(request.status)}
                            <span>{request.status?.replace('_', ' ') || 'Pending'}</span>
                          </span>
                        </td>
                        <td>
                          <div className="ref-actions">
                            <button
                              onClick={() => viewDetails(request, 'refund-request')}
                              className="btn btn-icon"
                              title="View details"
                            >
                              <Eye size={16} />
                            </button>
                            {request.status === 'PENDING_ADMIN' && (
                              <>
                                <button
                                  onClick={() => promptApprove(request.id)}
                                  className="btn btn-success btn-sm"
                                  title="Approve"
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={() => promptReject(request.id)}
                                  className="btn btn-danger btn-sm"
                                  title="Reject"
                                >
                                  <X size={14} />
                                </button>
                              </>
                            )}
                            {request.status === 'PENDING_MERCHANT' && (
                              <span className="ref-awaiting">Awaiting Merchant</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Pagination */}
        {!loading && pagination.total > 0 && (
          <div className="ref-pagination">
            <button
              onClick={() => setPagination({ ...pagination, current: pagination.current - 1 })}
              disabled={pagination.current === 1}
              className="btn btn-secondary btn-sm"
            >
              Previous
            </button>
            <span className="ref-page-info">
              Page {pagination.current} of {pagination.pages} ({pagination.total} total)
            </span>
            <button
              onClick={() => setPagination({ ...pagination, current: pagination.current + 1 })}
              disabled={pagination.current >= pagination.pages}
              className="btn btn-secondary btn-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {showModal && selectedItem && (
        <div className="ref-modal-overlay" onClick={closeModal}>
          <div className="ref-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ref-modal-header">
              <h2>
                {selectedItem.type === 'refund' ? (
                  <><ReceiptText size={20} /> Refund Details</>
                ) : (
                  <><MessageCircle size={20} /> Refund Request Details</>
                )}
              </h2>
              <button onClick={closeModal} className="btn btn-icon">
                <X size={20} />
              </button>
            </div>
            <div className="ref-modal-body">
              {selectedItem.type === 'refund' && (
                <>
                  <div className="ref-detail">
                    <h3>Refund Information</h3>
                    <div className="ref-detail-grid">
                      <div className="ref-detail-item">
                        <label>Refund ID</label>
                        <p className="ref-code">{selectedItem.id}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Amount</label>
                        <p className="ref-price">{formatCurrency(selectedItem.refundAmount)}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Order Number</label>
                        <p>{selectedItem.order?.orderNumber || 'N/A'}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Status</label>
                        <span className={`ref-badge ${selectedItem.status?.toLowerCase() || 'pending'}`}>
                          {getStatusIcon(selectedItem.status)}
                          <span>{selectedItem.status?.replace('_', ' ') || 'Pending'}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="ref-detail">
                    <h3>Customer</h3>
                    <div className="ref-detail-grid">
                      <div className="ref-detail-item">
                        <label>Name</label>
                        <p>{`${selectedItem.customer?.firstName || ''} ${selectedItem.customer?.lastName || ''}`.trim() || 'N/A'}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Email</label>
                        <p>{selectedItem.customer?.email || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ref-detail">
                    <h3>Reason</h3>
                    <p className="ref-reason">{selectedItem.refundReason || 'No reason provided'}</p>
                  </div>
                </>
              )}

              {selectedItem.type === 'refund-request' && (
                <>
                  <div className="ref-detail">
                    <h3>Request Information</h3>
                    <div className="ref-detail-grid">
                      <div className="ref-detail-item">
                        <label>Request ID</label>
                        <p className="ref-code">{selectedItem.id}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Amount</label>
                        <p className="ref-price">{formatCurrency(selectedItem.refundAmount)}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Order</label>
                        <p>#{selectedItem.order?.orderNumber || selectedItem.orderId?.slice(-8) || 'N/A'}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Status</label>
                        <span className={`ref-badge ${selectedItem.status?.toLowerCase() || 'pending'}`}>
                          {getStatusIcon(selectedItem.status)}
                          <span>{selectedItem.status?.replace('_', ' ') || 'Pending'}</span>
                        </span>
                      </div>
                      <div className="ref-detail-item">
                        <label>Date Requested</label>
                        <p>{formatDate(selectedItem.createdAt)}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ref-detail">
                    <h3>Customer</h3>
                    <div className="ref-detail-grid">
                      <div className="ref-detail-item">
                        <label>Name</label>
                        <p>{`${selectedItem.customer?.firstName || ''} ${selectedItem.customer?.lastName || ''}`.trim() || 'N/A'}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Email</label>
                        <p>{selectedItem.customer?.email || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ref-detail">
                    <h3>Merchant</h3>
                    <div className="ref-detail-grid">
                      <div className="ref-detail-item">
                        <label>Name</label>
                        <p>{selectedItem.merchant?.username || 'N/A'}</p>
                      </div>
                      <div className="ref-detail-item">
                        <label>Email</label>
                        <p>{selectedItem.merchant?.email || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                  <div className="ref-detail">
                    <h3>Reason for Refund</h3>
                    <p className="ref-reason">{selectedItem.refundReason || 'No reason provided'}</p>
                  </div>
                  {(selectedItem.merchantResponse || selectedItem.adminResponse || selectedItem.rejectionReason) && (
                    <div className="ref-detail ref-response">
                      <h3>Response History</h3>
                      {selectedItem.merchantResponse && (
                        <div className={`ref-response-item ${normalizeResponseAction(selectedItem.merchantResponse?.action || selectedItem.merchantResponse)}`}>
                          <strong>{getResponseTitle('Merchant', selectedItem.merchantResponse)}</strong>
                          <p>{typeof selectedItem.merchantResponse === 'string'
                            ? selectedItem.merchantResponse
                            : selectedItem.merchantResponse?.notes || 'Approved'}</p>
                        </div>
                      )}
                      {selectedItem.adminResponse && Object.keys(selectedItem.adminResponse || {}).length > 0 && (
                        <div className={`ref-response-item ${normalizeResponseAction(selectedItem.adminResponse?.action || selectedItem.adminResponse)}`}>
                          <strong>{getResponseTitle('Admin', selectedItem.adminResponse)}</strong>
                          <p>{selectedItem.adminResponse?.notes || selectedItem.adminResponse?.reason || 'Approved by admin'}</p>
                        </div>
                      )}
                      {selectedItem.rejectionReason && (
                        <div className="ref-response-item rejection">
                          <strong>Admin rejected</strong>
                          <p>{selectedItem.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="ref-modal-footer">
              {selectedItem.type === 'refund-request' && selectedItem.status === 'PENDING_ADMIN' && (
                <>
                  <button
                    onClick={() => {
                      closeModal();
                      promptApprove(selectedItem.id);
                    }}
                    className="btn btn-success"
                  >
                    <Check size={16} />
                    Approve & Process
                  </button>
                  <button
                    onClick={() => {
                      closeModal();
                      promptReject(selectedItem.id);
                    }}
                    className="btn btn-danger"
                  >
                    <X size={16} />
                    Reject
                  </button>
                </>
              )}
              <button onClick={closeModal} className="btn btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmAction && (
        <div className="ref-modal-overlay" onClick={cancelAction}>
          <div className="ref-modal ref-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="ref-modal-header">
              <h2>{confirmAction.title}</h2>
              <button onClick={cancelAction} className="btn btn-icon">
                <X size={20} />
              </button>
            </div>
            <div className="ref-modal-body">
              <p className="ref-confirm-msg">{confirmAction.message}</p>
              <div className="ref-form-group">
                <label>
                  {confirmAction.action === 'approve' || confirmAction.action === 'bulk-approve'
                    ? 'Notes (optional)'
                    : 'Reason (required)'}
                </label>
                <textarea
                  value={actionNotes}
                  onChange={(e) => setActionNotes(e.target.value)}
                  placeholder={
                    confirmAction.action === 'approve' || confirmAction.action === 'bulk-approve'
                      ? 'Add any notes about this approval...'
                      : 'Please provide a reason for rejection...'
                  }
                  rows="4"
                  autoFocus
                />
              </div>
            </div>
            <div className="ref-modal-footer">
              <button onClick={cancelAction} className="btn btn-secondary">
                Cancel
              </button>
              <button
                onClick={executeAction}
                className={`btn ${confirmAction.confirmClass}`}
              >
                {confirmAction.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Refunds;
